import { Component, OnInit, Input, Output, EventEmitter, forwardRef, ElementRef } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
import { colorutils } from '../colorutils';
import { TranslationService } from '../../services/translation.service';


@Component({
  selector: "app-multi-list-dialog",
  templateUrl: './multi-list-dialog.component.html',
  styles: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiListDialogComponent),
      multi: true
    },
  ],
})
export class MultiListDialogComponent implements OnInit, ControlValueAccessor {


  @Output() Changed = new EventEmitter();

  private _enabled: boolean = true;
  private onChange: Function;
  private onTouched: Function;
  protected disabled: boolean;
  protected colors: colorutils = colorutils;

  protected all_selected: boolean = false;
  protected none_selected: boolean = false;


  constructor(private translation_service: TranslationService) {
    this.onChange = (_: any) => { };
    this.onTouched = () => { };
  }

  ngOnInit() {
  }

  // if true, all the items are visually checked if none is really selected
  @Input()
  public check_none_selected: boolean = false;


  @Input()
  public item_icon: string;


  @Input()
  public mono_select: boolean = false;

  /*
   * The original list without filtering
   */
  public _list: any[];
  get list() {
    return this._list;
  }
  @Input() set list(val) {
    this._list = val;
    this.filterList();
  }

  /**
   * The list filtered according with the parent
   **/
  public _filtered_list: any[] = [];
  get filtered_list() {
    return this._filtered_list;
  }
  @Input() set filtered_list(val) {
    this._filtered_list = val;
  }

  /**
   * The selected items
   * **/
  @Input('filter')
  public _filter: any[] = [];

  get value() {
    return this._filter;
  }
  set value(val) {
    this._filter = val;
    this.updateSelectedItems();
    this.onChange(val);
    this.onTouched();
  }

  private _child: MultiListDialogComponent = null;
  get child() {
    return this._child;
  }
  @Input() set child(val) {
    this._child = val;
  }

  private _parent: MultiListDialogComponent = null;
  get parent() {
    return this._parent;
  }
  @Input() set parent(val: MultiListDialogComponent) {
    this._parent = val;
    this.parent._child = this;
    this.filterList();
  }


  get enabled(): boolean {
    return this._enabled;
  }
  @Input() set enabled(value: boolean) {
    this._enabled = value;
  }

  writeValue(filter: any[]): void {
    if (!filter)
      filter = [];
    this._filter = filter;
    this.updateSelectedItems();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.enabled = !disabled;
  }

  protected onChanged() {
    this.Changed.emit(this._filter);
  }

  private validateChange() {
    this.checkSelection();
    this.onChange(this._filter);
    this.onChanged();
    this.onTouched();
  }


  /**
   * Filters the list accordiong with the parent selection
   * */
  private filterList(): any {
    if (!this._filtered_list)
      this._filtered_list = [];
    this._filtered_list.splice(0, this._filtered_list.length);
    if (!this.parent) {
      for (let current of this._list)
        this._filtered_list.push(current);
    }
    // Auto-select : if no parent selection, deselect all
    else if (this.parent._filter.length == 0) {
      for (let current of this._list) {
        this._filtered_list.push(current);
        current.selected = false;
        this._filter.splice(this._filter.indexOf(current.id), 1)
      }
      if (this._child)
        this._child.filterList();
    }
    else {
      // Auto-select : if there is only one parent selection
      let previous_filter_list = [];
      if (this.parent._filter.length == 1)
        for (let fil of this._filtered_list)
          previous_filter_list.push(fil);
      for (let current of this._list)
        if (this.parent._filter.find(f => f == current.second + "")) {
          this._filtered_list.push(current);
          // Auto-select : if it's a new item, we select it automatically
          if (!previous_filter_list.find(f => f.id == current.id + "")) {
            current.selected = true;
            this._filter.push(current.id);
          }
        }
        else {
          let found = this._filter.find(x => x == current.id + "");
          if (found) {
            found.selected = false;
            this._filter.splice(this._filter.indexOf(found), 1)
          }
        }
      if (this._child)
        this._child.filterList();
    }
    this.updateSelectedItems();
    this.checkSelection();
  }


  private selectItem(item: any, select: boolean) {
    if (this.mono_select) {
      for (let last of this._filter)
        last.selected = false;
      item.selected = true;
      this._filter = [item];
    } else {
      item.selected = select;
      let found = this._filter.find(x => x == item.id);
      if (select) {
        if (!found)
          this._filter.push(item);
      }
      else {
        if (found)
          this._filter.splice(this._filter.indexOf(found), 1)
      }
    }
  }

  /**
   * Occurs when an item is select/deselected
   * @param item
   */
  protected onClickItem(item) {
    if (this.mono_select) {
      for (let last of this._filter)
        last.selected = false;
      item.selected = true;
      this._filter = [item];
    } else {
      let found = this._filter.find(x => x == item.id);
      if (found) {
        item.selected = false;
        this._filter.splice(this._filter.indexOf(found), 1)
      } else {
        item.selected = true;
        this._filter.push(item.id);
      }
    }
    if (this._child)
      this._child.filterList();
    this.validateChange();
  }

  private checkSelection() {
    this.none_selected = this._filter.length == 0;
    this.all_selected = this._filter.length == this.filtered_list.length;
  }

  /**
   * Selects all all or noe of the items
   * @param select
   */
  protected onSelectAll(select: boolean) {
    this._filter.splice(0, this._filter.length);
    for (let item of this._filtered_list) {
      item.selected = select;
      if (select)
        this._filter.push(item.id + "");
    }
    if (this._child)
      this._child.filterList();
    this.validateChange();
  }

  /**
   * Gets the ids of the selected items
   * */
  public getSelectedIds() {
    let ids: any[] = [];
    for (let filtered of this._filter)
      ids.push(filtered);
    return ids.length == 0 ? [] : ids;
  }

  public selectIds(...ids: string[]) {
    for (let id of ids) {
      for (let item of this._filtered_list) {
        if (item.id == id) {
          item.selected = true;
          if (!this._filter.find(x => x == id))
            this._filter.push(item);
          break;
        }
      }
    }
    this.validateChange();
    if (this._child)
      this._child.filterList();
  }

  private updateSelectedItems() {
    for (let item of this._filtered_list)
      item.selected = this._filter.includes(item.id);
  }

  /**
   * Gets the names of the selected items (optionaly surrounded with <mark>)
   * */
  public getSelectedNames(with_mark: boolean) {
    let names: any[] = [];
    for (let filtered of this._filtered_list) {
      if (filtered.selected)
        names.push(with_mark ? "<mark>" + filtered.name + "</mark> " : filtered.name);
    }
    return names;
  }

  public getSelectedCount() {
    return this._filter.length;
  }


  /**
   * Gets the names of the selected items (take care of the parents recursivly) (optionaly surrounded with <mark>)
   * */
  public getFinalNames(with_mark: boolean) {
    if (this._filter.length == 0)
      if (this.parent)
        return this.parent.getFinalNames(with_mark);
      else if (this._list.length == 1) // case where the first list has only one item (one building in search page)
        return [with_mark ? "<mark>" + this._list[0].name + "</mark> " : this._list[0].name];
      else
        return [];
    return this.getSelectedNames(with_mark);
  }

}